import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Link,
  LinearProgress,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  StateReportModel,
  StateReportDocument,
} from "../../models/StateReportModel";
import { apiClient } from "../../helper/api";
import useAuth from "../../context/AuthProvider";
import { formatUtcToLocal } from "../../helper/utils";
import formatPrice from "../../helper/currencyFormatter";
import Swal from "sweetalert2";
import moment from "moment";
import { GlobalStyles } from "@mui/material";

const urlRegex = /(https?:\/\/[^\s]+)/g;

/** Extracts URLs from instructions and returns instructions text without URLs plus the first URL for the Filing URL box. */
function stripUrlsFromInstructions(
  text: string | undefined
): { instructionsOnly: string; filingUrl: string } {
  if (text == null || text === "") return { instructionsOnly: "", filingUrl: "" };
  const urls = text.match(urlRegex) || [];
  const firstUrl = urls[0];
  const filingUrl =
    firstUrl && firstUrl.toUpperCase() !== "N/A" ? firstUrl.trim() : "";
  const instructionsOnly = text.replace(urlRegex, "").replace(/\n{3,}/g, "\n\n").trim();
  return { instructionsOnly, filingUrl };
}

/** Renders text with URLs as clickable links (preserves newlines). */
function instructionsWithClickableLinks(text: string | undefined): React.ReactNode {
  if (text == null || text === "") return "N/A";
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("http://") || part.startsWith("https://") ? (
          <Link
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ wordBreak: "break-all", color: "primary.main" }}
          >
            {part}
          </Link>
        ) : (
          part
        )
      )}
    </>
  );
}

interface StateReportPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  stateReport: StateReportModel | null;
  onMarkAsDone?: () => void;
  selectedTab?: number;
  states?: { stateId: number; stateName: string; postalAbbv?: string }[];
}

export const StateReportPreviewDialog: React.FC<
  StateReportPreviewDialogProps
> = ({
  open,
  onClose,
  stateReport,
  onMarkAsDone,
  selectedTab = 1,
  states = [],
}) => {
  const [documents, setDocuments] = useState<StateReportDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(
    null,
  );
  const [documentViewerFileName, setDocumentViewerFileName] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const token = user?.token;
  const dialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && stateReport?.stateReportId) {
      fetchDocuments();
    } else {
      setDocuments([]);
    }
  }, [open, stateReport?.stateReportId]);

  const fetchDocuments = async () => {
    if (!stateReport?.stateReportId) return;

    setLoadingDocuments(true);
    if(stateReport?.documents && stateReport?.documents?.length > 0){
      setDocuments(stateReport?.documents);
      setLoadingDocuments(false);
      return;
    }
    try {
      const res = await apiClient(
        `/StateReport/getStateReportDocuments?stateReportId=${stateReport.stateReportId}`,
        {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
        },
      );

      if (res && res.status && res.data) {
        setDocuments(res.data);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleViewDocument = async (blobAddress: string, fileName?: string) => {
    if (!blobAddress) return;

    // Download the file into memory first, then display it
    setLoading(true);
    setDocumentViewerOpen(true); // Open dialog immediately to show loading
    setDocumentViewerFileName(fileName || "document.pdf");

    try {
      console.log("Downloading Azure blob:", blobAddress);

      // Step 1: Download the file from Azure blob storage
      const response = await fetch(blobAddress, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error(
          `Failed to download file: ${response.status} ${response.statusText}`,
        );
      }

      // Step 2: Get content type from response headers
      let contentType = "application/pdf";
      contentType = contentType.split(";")[0].trim();

      console.log("Content type:", contentType);

      // Step 3: Download the entire file into memory as a blob
      const downloadedBlob = await response.blob();
      console.log("File downloaded, size:", downloadedBlob.size, "bytes");

      // Step 4: Create a clean blob with explicit content type
      // This removes any Content-Disposition headers that might force download
      const cleanBlob = new Blob([downloadedBlob], {
        type: contentType,
      });

      // Step 5: Create a blob URL from the downloaded file
      // This blob URL is stored in memory and can be displayed inline
      const blobUrl = URL.createObjectURL(cleanBlob);
      console.log("Blob URL created:", blobUrl);

      // Step 6: Set the blob URL to display in the viewer
      setDocumentViewerUrl(blobUrl);
      setLoading(false);
    } catch (error) {
      console.error("Error downloading Azure blob:", error);
      setLoading(false);
      setDocumentViewerOpen(false);

      // If download fails, show error and fall back to opening in new tab
      Swal.fire({
        title: "Error",
        text: "Failed to download file. Opening in new tab instead.",
        icon: "warning",
        confirmButtonText: "OK",
      }).then(() => {
        window.open(blobAddress, "_blank");
      });
    }
  };

  const handleCloseDocumentViewer = () => {
    if (documentViewerUrl) {
      URL.revokeObjectURL(documentViewerUrl);
    }
    setDocumentViewerOpen(false);
    setDocumentViewerUrl(null);
    setDocumentViewerFileName(null);
  };
  const handleDownloadDocument = (doc: StateReportDocument) => {
    if (doc.blobAddress) {
      const link = document.createElement("a");
      link.href = doc.blobAddress;
      link.download = doc.documentName || "document.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      // Prepare documents array with documentName and blobAddress
      const documentsPayload = documents
        .filter((doc) => doc.documentName && doc.blobAddress)
        .map((doc) => ({
          documentName: doc.documentName || "",
          blobAddress: doc.blobAddress || "",
        }));

      if (documentsPayload.length === 0) {
        Swal.fire({
          title: "Error",
          text: "No valid documents to download.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setDownloadingAll(false);
        return;
      }

      const response = await apiClient(
        "/StateReport/generateComprehensiveStateReportDocumentsZip",
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            documents: documentsPayload,
          }),
        },
      );

      if (response?.status && response?.data) {
        // Handle different response types
        if (response.data.zip_url) {
          // Direct URL download
          window.open(response.data.zip_url, "_blank");
        } else if (response.data.zipBase64) {
          // Base64 encoded ZIP
          const byteCharacters = atob(response.data.zipBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/zip" });

          const fileName = `StateReportDocuments_${stateReport?.stateReportId}_${new Date().toISOString().split("T")[0]}.zip`;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          Swal.fire({
            title: "Warning",
            text:
              response?.message ||
              "Download initiated, but no file data received.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }

        Swal.fire({
          title: "Success!",
          text: response?.message || "All documents downloaded successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        throw new Error(response?.message || "Failed to generate ZIP file.");
      }
    } catch (error: any) {
      console.error("Error downloading all documents:", error);
      Swal.fire({
        title: "Error",
        text:
          error?.message || "Failed to download documents. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleMarkAsDone = () => {
    if (!stateReport?.stateReportId) return;

    const isCompletedTab = selectedTab === 2;
    const actionText = isCompletedTab
      ? "Return to Outstanding"
      : "mark as Completed";
    const statusText = isCompletedTab ? "Revised" : "Completed";
    const successText = isCompletedTab
      ? "The report has been returned to Outstanding."
      : "The report has been marked as Completed.";

    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${actionText}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        apiClient(`/StateReport/UpdateClientStateReportStatus`, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: stateReport.stateReportId,
            status: statusText,
          }),
        })
          .then((res) => {
            if (!res) return;
            if (res.status) {
              Swal.fire({
                title: "Success!",
                text: successText,
                icon: "success",
              });
              if (onMarkAsDone) {
                onMarkAsDone();
              }
              onClose();
            } else {
              Swal.fire({
                title: "Error!",
                text: res.message || `Failed to ${actionText}.`,
                icon: "error",
              });
            }
          })
          .catch((error) => {
            console.error(`Error ${actionText}:`, error);
            Swal.fire({
              title: "Error!",
              text: `Failed to ${actionText}.`,
              icon: "error",
            });
          });
      }
    });
  };

  if (!stateReport) return null;

  const formatCurrency = (value: string | number | undefined): string => {
    if (!value) return "$0.00";
    const numValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[,\$]/g, "").trim())
        : parseFloat(String(value));
    return isNaN(numValue) ? "$0.00" : formatPrice(numValue.toString());
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return " ";
    return formatUtcToLocal(dateString, "MM/DD/YYYY");
  };

  const isDueDatePast = (dueDate: string | undefined): boolean => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const today = new Date();
    return due < today;
  };

  const { instructionsOnly, filingUrl } = stripUrlsFromInstructions(stateReport?.instructions);
  const dueDatePast = isDueDatePast(stateReport?.dueDate);
  const stateName =
    states.find((state) => state.stateId === stateReport.stateId)?.stateName || "N/A";
  const dueDateText = stateReport.dueDate
    ? formatDate(stateReport.dueDate)
    : "N/A";

  const sectionTitleSx = {
    mb: 1.5,
    color: "#0d4a63",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  };

  const screenCardSx = {
    borderRadius: 2,
    border: "1px solid #d9e4ec",
    // boxShadow: "0 12px 28px rgba(13,74,99,0.08)",
    overflow: "hidden",
    "@media print": {
      border: "none",
      boxShadow: "none",
      borderRadius: 0,
    },
  };

  const detailRowSx = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    py: 1.25,
    borderBottom: "1px solid #e7eef4",
    "@media print": {
      py: 0.5,
      borderBottom: "none",
    },
  };

  const summaryItems = [
    { label: "State", value: stateName, emphasize: true },
    { label: "Report Name", value: stateReport.name || "N/A" },
    { label: "Period", value: stateReport.period || "N/A" },
    { label: "Due Date", value: dueDateText, emphasize: true, danger: dueDatePast },
    { label: "Licensee Name", value: stateReport.licensee || "N/A" },
    { label: "Client Name", value: stateReport.client || stateReport.partnerName || "N/A" },
  ];

  const paymentItems = [
    ...(stateReport?.paymentType
      ? [{ label: "Payment Type", value: stateReport.paymentType || "N/A" }]
      : []),
    { label: "Pay To", value: stateReport.payTo || "N/A" },
    { label: "Address", value: stateReport.address || "N/A", preserveWhitespace: true },
  ];

  const statusItems = [
    {
      label: "MISC Sent to Client On",
      value: stateReport?.dateFinalReportSentToClient
        ? formatDate(stateReport.dateFinalReportSentToClient)
        : formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"),
    },
    {
      label: "Client Marked as Done On",
      value: stateReport?.doneByClientAt
        ? formatDate(stateReport.doneByClientAt)
        : "N/A",
    },
    {
      label: "By Client User",
      value: stateReport?.filerName || "N/A",
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          ".print-documents-section": {
            marginTop: "20px",
          },
          /* Force document table sharp corners on screen (first-image UI) */
          ".print-documents-section .print-document-table": {
            borderRadius: "0 !important",
            overflow: "hidden",
          },
          ".print-documents-section .print-document-table .MuiDataGrid-root": {
            borderRadius: "0 !important",
          },
          ".print-documents-section .print-document-table .MuiDataGrid-columnHeaders": {
            borderRadius: "0 !important",
          },
          ".print-documents-section .print-document-table .MuiDataGrid-columnHeader": {
            borderRadius: "0 !important",
            borderTopLeftRadius: "0 !important",
            borderTopRightRadius: "0 !important",
            borderBottomLeftRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
          },
          /* On screen: only DataGrid (3 columns) must show; hide print-only table */
          ".print-only-document-table": {
            position: "absolute !important",
            left: "-9999px !important",
            width: "0 !important",
            height: "0 !important",
            overflow: "hidden !important",
            visibility: "hidden !important",
            display: "none !important",
          },
          "@media print": {
            "body *": {
              visibility: "hidden !important",
            },
            ".MuiDialog-root, .MuiDialog-paper": {
              position: "static !important",
              margin: "0 !important",
              padding: "0 !important",
              width: "100% !important",
              maxWidth: "none !important",
              boxShadow: "none !important",
              borderRadius: "0 !important",
              border: "none !important",
              maxHeight: "none !important",
              height: "auto !important",
              visibility: "visible !important",
            },
            ".MuiDialog-paper *": {
              visibility: "visible !important",
            },
            ".MuiDialogTitle-root": {
              display: "block !important",
              position: "relative",
              backgroundColor: "#2099d2 !important",
              color: "#ffffff !important",
              padding: "8px 12px !important",
              textAlign: "center !important",
              fontWeight: "bold !important",
              fontSize: "14pt !important",
              WebkitPrintColorAdjust: "exact !important",
              printColorAdjust: "exact !important",
            },
            ".MuiDialogTitle-root *": {
              color: "#ffffff !important",
            },
            ".print-area": {
              position: "relative",
              width: "100% !important",
              margin: 0,
              padding: 0,
              backgroundColor: "white !important",
              fontSize: "10pt !important",
              lineHeight: "1.35 !important",
            },
            ".print-area *": {
              color: "#000000 !important",
            },
            // Keep Due Date red in print
            ".due-date-label, .due-date-value": {
              color: "#d32f2f !important",
            },
            ".MuiDialogContent-root": {
              padding: "0 !important",
              margin: "0 !important",
              overflow: "visible !important",
              maxHeight: "none !important",
            },
            ".MuiDialogActions-root": {
              display: "none !important",
            },
            "button, .MuiIconButton-root, .MuiCircularProgress-root": {
              display: "none !important",
            },
            ".hide-on-print": {
              display: "none !important",
            },
            "*": {
              WebkitPrintColorAdjust: "exact !important",
              printColorAdjust: "exact !important",
              colorAdjust: "exact !important",
            },
            // Preserve background colors
            "[style*='background-color: #E3F2FD'], [style*='background-color:#E3F2FD']": {
              backgroundColor: "#E3F2FD !important",
            },
            "[style*='background-color: yellow'], [style*='background-color:yellow']": {
              backgroundColor: "#FFFF00 !important",
            },
            ".print-area [style*='E3F2FD']": {
              padding: "6px 10px !important",
              marginBottom: "6px !important",
            },
            ".print-area [style*='yellow']": {
              padding: "6px 10px !important",
              marginBottom: "4px !important",
            },
            // Table styles
            ".MuiDataGrid-root": {
              borderCollapse: "collapse !important",
              width: "100% !important",
              fontSize: "11pt !important",
              tableLayout: "fixed !important",
            },
            ".MuiDataGrid-cell, .MuiDataGrid-columnHeader": {
              border: "none !important",
              padding: "10px 12px !important",
              color: "#000000 !important",
              fontSize: "11pt !important",
              wordWrap: "break-word !important",
              wordBreak: "break-word !important",
              overflow: "visible !important",
              whiteSpace: "normal !important",
              lineHeight: "1.4 !important",
              verticalAlign: "top !important",
            },
            // Ensure column headers and cells have matching widths
            ".MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
              boxSizing: "border-box !important",
            },
            // Type column - fixed width, left aligned (same for header and cell)
            ".MuiDataGrid-columnHeader[data-field='type']": {
              width: "250px !important",
              minWidth: "250px !important",
              maxWidth: "250px !important",
              textAlign: "left !important",
              color: "#ffffff !important",
            },
            ".MuiDataGrid-cell[data-field='type']": {
              width: "250px !important",
              minWidth: "250px !important",
              maxWidth: "250px !important",
              textAlign: "left !important",
              color: "#000000 !important",
            },
            // Document Name column - flexible width, left aligned (same for header and cell)
            ".MuiDataGrid-columnHeader[data-field='documentName']": {
              width: "auto !important",
              minWidth: "300px !important",
              flex: "1 1 0% !important",
              textAlign: "left !important",
              color: "#ffffff !important",
              fontWeight: "bold !important",
            },
            ".MuiDataGrid-cell[data-field='documentName']": {
              width: "auto !important",
              minWidth: "300px !important",
              flex: "1 1 0% !important",
              textAlign: "left !important",
              color: "#000000 !important",
              fontWeight: "normal !important",
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: "#1976D2 !important",
              color: "#ffffff !important",
              display: "table-row !important",
            },
            ".MuiDataGrid-columnHeaders *": {
              color: "#ffffff !important",
            },
            ".MuiDataGrid-row": {
              display: "table-row !important",
              pageBreakInside: "avoid !important",
            },
            ".MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fafafa !important",
            },
            // Ensure all cell content is visible and properly aligned
            ".MuiDataGrid-cellContent": {
              overflow: "visible !important",
              textOverflow: "clip !important",
              whiteSpace: "normal !important",
              wordWrap: "break-word !important",
              color: "#000000 !important",
              textAlign: "left !important",
              width: "100% !important",
            },
            // Override Box alignment in cells for print
            ".MuiDataGrid-cell .MuiBox-root": {
              justifyContent: "flex-start !important",
              textAlign: "left !important",
              width: "100% !important",
            },
            // Ensure proper column header alignment
            ".MuiDataGrid-columnHeaderTitle": {
              textAlign: "left !important",
              fontWeight: "bold !important",
            },
            // Increase font size for Additional Notes and Instructions sections
            ".print-area strong": {
              fontSize: "13pt !important",
              fontWeight: "bold !important",
            },
            // Increase font size for content in Instructions and Additional Notes
            ".print-area > .MuiBox-root > .MuiBox-root": {
              fontSize: "13pt !important",
              lineHeight: "1.6 !important",
            },
            // Hide download column when printing
            ".MuiDataGrid-columnHeader[data-field='download'], .MuiDataGrid-cell[data-field='download']": {
              display: "none !important",
            },
            // Make sure table doesn't break across pages awkwardly
            ".MuiDataGrid-main": {
              pageBreakInside: "auto !important",
            },
            // Ensure proper table structure
            ".MuiDataGrid-virtualScroller": {
              overflow: "visible !important",
            },
            ".MuiDataGrid-virtualScrollerContent": {
              width: "100% !important",
            },
            // Fix column header alignment and ensure matching widths
            ".MuiDataGrid-columnHeader": {
              textAlign: "left !important",
              display: "table-cell !important",
            },
            ".MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "flex-start !important",
            },
            // Ensure cells align perfectly
            ".MuiDataGrid-cell": {
              display: "table-cell !important",
            },
            ".print-documents-section.no-documents": {
              display: "none !important",
            },
            ".print-documents-section.has-documents": {
              pageBreakBefore: "avoid !important",
              marginTop: "8px !important",
            },
            /* Compact content above Document Attachments so it fits on first page */
            ".print-area > .MuiBox-root": {
              marginBottom: "4px !important",
            },
            ".print-area .MuiGrid-root": {
              marginTop: "0 !important",
              marginBottom: "0 !important",
            },
            ".print-only-document-table": {
              display: "block !important",
              visibility: "visible !important",
              width: "100% !important",
              borderRadius: "0 !important",
            },
            ".print-only-document-table table": {
              width: "100% !important",
              backgroundColor: "#ffffff !important",
              WebkitPrintColorAdjust: "exact !important",
              printColorAdjust: "exact !important",
              borderRadius: "0 !important",
            },
            ".print-only-document-table thead th": {
              backgroundColor: "#1976D2 !important",
              color: "#ffffff !important",
              borderRadius: "0 !important",
            },
            ".print-only-document-table td": {
              color: "#000000 !important",
            },
            ".print-wrapper.single-page": {
              pageBreakInside: "avoid !important",
              breakInside: "avoid !important",
              pageBreakBefore: "avoid !important",
              pageBreakAfter: "avoid !important",
              overflow: "hidden !important",
              maxHeight: "100vh !important",
              height: "auto !important",
              margin: "0 !important",
              padding: "0 !important",
            },
            ".print-wrapper.single-page *": {
              pageBreakInside: "avoid !important",
              breakInside: "avoid !important",
              pageBreakBefore: "avoid !important",
              pageBreakAfter: "avoid !important",
              marginBottom: "0 !important",
              paddingBottom: "0 !important",
            },
            "body, html": {
              margin: "0 !important",
              padding: "0 !important",
              height: "auto !important",
              overflow: "hidden !important",
            },
            ".MuiDialog-root, .MuiDialog-paper, .MuiDialogContent-root": {
              margin: "0 !important",
              padding: "0 !important",
              width: "100% !important",
              maxWidth: "100% !important",
              boxShadow: "none !important",
              borderRadius: "0 !important",
            },
            "@page": {
              size: "letter",
              margin: 0,
            },
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: "no-round-corner",
          sx: {
            width: "90%",
            maxWidth: 1200,
            minHeight: "50vh",
            maxHeight: "90vh",
            borderRadius: 2,
            boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
            overflow: "hidden",
            "@media print": {
              padding: "0 !important",
              maxHeight: "none",
              borderRadius: 0,
              boxShadow: "none",
            },
          },
        }}
        sx={{ "& .MuiDialog-paper": { overflow: "hidden" } }}
      >
        {loadingDocuments && <LinearProgress sx={{ flexShrink: 0 }} />}
        <DialogTitle
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 2,
            px: 3,
            background: "linear-gradient(135deg, #0d4a63 0%, #15607a 100%)",
            color: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            "@media print": {
              backgroundColor: "#2099d2 !important",
              background: "none",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssessmentOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
            <Typography
              component="span"
              variant="h6"
              sx={{ fontWeight: 600, letterSpacing: "0.02em" }}
            >
              Notice of State Report
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            className="hide-on-print"
            sx={{
              color: "rgba(255,255,255,0.9)",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
              },
            }}
            aria-label="Close dialog"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: 0,
            py: 0,
            overflow: "auto",
            "@media print": {
              padding: "0 !important",
            },
          }}
        >
          <Box
            className={`print-wrapper ${documents.length === 0 ? "single-page" : ""}`}
            sx={{
              p: 3,
              backgroundColor: "#f5f8fb",
              "@media print": {
                p: 0,
                backgroundColor: "#ffffff",
              },
            }}
          >
            <Box ref={dialogContentRef} className="print-area">
              <Grid
                container
                spacing={3}
                sx={{
                  "@media print": {
                    rowGap: 0,
                    columnGap: 0,
                  },
                }}
              >
                <Grid item xs={12}>
                  <Typography variant="overline" sx={sectionTitleSx}>
                    Report Summary
                  </Typography>

                  <Paper
                    variant="outlined"
                    sx={{
                      ...screenCardSx,
                      backgroundColor: "#eaf5fb",
                      p: 3,
                      "@media print": {
                        backgroundColor: "#E3F2FD !important",
                        p: "6px 10px",
                      },
                    }}
                  >
                    
                    <Grid container>
                      {summaryItems.map((item) => (
                        <Grid item xs={12} md={6} key={item.label} sx={{
                          "&:last-child .MuiBox-root": {
                            borderBottom: "none",
                          },
                          "&:nth-child(5) .MuiBox-root": {
                            borderBottom: "none",
                          },
                        }}>
                          <Box
                            sx={{
                              ...detailRowSx,
                              py: 0.75,
                              justifyContent: "flex-start",
                            }}
                          >
                            <Typography
                              className={item.label === "Due Date" ? "due-date-label" : undefined}
                              sx={{
                                fontWeight: 700,
                                color: item.danger ? "#d32f2f" : "#0d4a63",
                                minWidth: { xs: 120, sm: 160 },
                                fontSize: item.emphasize ? "1rem" : "0.95rem",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              className={item.label === "Due Date" ? "due-date-value" : undefined}
                              sx={{
                                fontWeight: item.emphasize ? 700 : 500,
                                color: item.danger ? "#d32f2f" : "text.primary",
                                textAlign: "right",
                                whiteSpace: "pre-line",
                                fontSize: item.emphasize ? "1rem" : "0.95rem",
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography variant="overline" sx={sectionTitleSx}>
                    Filing Details
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      ...screenCardSx,
                      p: 3,
                      "@media print": {
                        p: "4px 12px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        backgroundColor: "#fff59d",
                        border: "1px solid #f4d03f",
                        borderRadius: 2,
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        "@media print": {
                          backgroundColor: "#FFFF00 !important",
                          border: "none",
                          borderRadius: 0,
                          px: 1,
                          py: 0.5,
                          mb: 1,
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: "#5f4700" }}>
                        Tax Amount
                      </Typography>
                      <Typography sx={{ fontWeight: 700, color: "#5f4700" }}>
                        {stateReport?.taxAmount
                          ? formatCurrency(stateReport.taxAmount)
                          : "N/A"}
                      </Typography>
                    </Box>

                    {paymentItems.map((item, index) => (
                      <Box
                        key={item.label}
                        sx={{
                          ...detailRowSx,
                          borderBottom:
                            index === paymentItems.length - 1
                              ? "none"
                              : detailRowSx.borderBottom,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#0d4a63",
                            minWidth: { xs: 100, sm: 130 },
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: "right",
                            whiteSpace: item.preserveWhitespace ? "pre-line" : "normal",
                            flex: 1,
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography variant="overline" sx={sectionTitleSx}>
                    Instructions And Notes
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      ...screenCardSx,
                      p: 3,
                      "@media print": {
                        p: "4px 12px",
                      },
                    }}
                  >
                    <Box sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontWeight: 700, color: "#0d4a63", mb: 1 }}>
                        Instructions
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #d9e4ec",
                          borderRadius: 2,
                          backgroundColor: "#fbfdff",
                          p: 2,
                          whiteSpace: "pre-line",
                          lineHeight: 1.7,
                          minHeight: 88,
                          "@media print": {
                            border: "none",
                            backgroundColor: "transparent",
                            borderRadius: 0,
                            p: "2px 0",
                            minHeight: "auto",
                            lineHeight: 1.15,
                          },
                        }}
                      >
                        {instructionsOnly || "N/A"}
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontWeight: 700, color: "#0d4a63", mb: 1 }}>
                        Filing URL
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #d9e4ec",
                          borderRadius: 2,
                          backgroundColor: "#fbfdff",
                          p: 2,
                          minHeight: 64,
                          display: "flex",
                          alignItems: "center",
                          "@media print": {
                            border: "none",
                            backgroundColor: "transparent",
                            borderRadius: 0,
                            p: "2px 0",
                            minHeight: "auto",
                          },
                        }}
                      >
                        <Typography
                          component="div"
                          sx={{
                            wordBreak: "break-word",
                            "& a": { color: "primary.main" },
                          }}
                        >
                          {filingUrl ? instructionsWithClickableLinks(filingUrl) : "N/A"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ fontWeight: 700, color: "#0d4a63", mb: 1 }}>
                        Additional Notes
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #d9e4ec",
                          borderRadius: 2,
                          backgroundColor: "#fbfdff",
                          p: 2,
                          whiteSpace: "pre-line",
                          lineHeight: 1.7,
                          minHeight: 88,
                          "@media print": {
                            border: "none",
                            backgroundColor: "transparent",
                            borderRadius: 0,
                            p: "2px 0",
                            minHeight: "auto",
                            lineHeight: 1.35,
                          },
                        }}
                      >
                        {stateReport?.additionalNotes || "N/A"}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    className={`print-documents-section ${
                      documents.length === 0 ? "no-documents" : "has-documents"
                    }`}
                    sx={{ overflow: "visible" }}
                  >
                    <Typography variant="overline" sx={sectionTitleSx}>
                      Document Attachments
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        ...screenCardSx,
                        p: 3,
                        "@media print": {
                          p: "4px 0",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Attached files for this state report
                        </Typography>
                        {documents.length > 0 && (
                          <Button
                            className="hide-on-print"
                            variant="outlined"
                            startIcon={
                              downloadingAll ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadIcon />
                              )
                            }
                            onClick={handleDownloadAll}
                            disabled={downloadingAll}
                            sx={{
                              borderColor: "#2099d2",
                              color: "#15607a",
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                          >
                            {downloadingAll ? "Downloading..." : "Download All"}
                          </Button>
                        )}
                      </Box>

                      {loadingDocuments ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 160,
                          }}
                        >
                          <CircularProgress size={28} />
                        </Box>
                      ) : documents.length > 0 ? (
                        (() => {
                          const documentRows = documents.map((doc, index) => ({
                            id: doc.stateReportDocumentId || index,
                            type: doc.documentType || "N/A",
                            documentName: doc.documentName || "N/A",
                            blobAddress: doc.blobAddress || "",
                          }));

                          const columns: GridColDef[] = [
                            {
                              field: "download",
                              headerName: "Actions",
                              width: 180,
                              align: "center",
                              headerAlign: "center",
                              sortable: false,
                              filterable: false,
                              renderCell: (params) => (
                                <Box
                                  className="hide-on-print"
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleViewDocument(
                                        params.row.blobAddress,
                                        params.row.documentName,
                                      )
                                    }
                                    disabled={!params.row.blobAddress}
                                    title="View"
                                    sx={{
                                      border: "1px solid #d9e4ec",
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#f4f8fb",
                                      },
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadDocument(params.row)}
                                    disabled={!params.row.blobAddress}
                                    title="Download"
                                    sx={{
                                      border: "1px solid #d9e4ec",
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#f4f8fb",
                                      },
                                    }}
                                  >
                                    <CloudDownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ),
                            },
                            {
                              field: "type",
                              headerName: "Type",
                              width: 250,
                              align: "left",
                              headerAlign: "left",
                              renderCell: (params) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    fontWeight: 500,
                                  }}
                                >
                                  {params.value}
                                </Box>
                              ),
                            },
                            {
                              field: "documentName",
                              headerName: "Document Name",
                              flex: 1,
                              minWidth: 300,
                              align: "left",
                              headerAlign: "left",
                              renderCell: (params) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  {params.value}
                                </Box>
                              ),
                            },
                          ];

                          return (
                            <Box sx={{ position: "relative", width: "100%", display: "block" }}>
                              <Box
                                className="print-only-document-table"
                                sx={{
                                  position: "absolute",
                                  left: "-9999px",
                                  top: 0,
                                  width: 0,
                                  height: 0,
                                  overflow: "hidden",
                                  visibility: "hidden",
                                  pointerEvents: "none",
                                  "@media print": {
                                    position: "static !important",
                                    left: "auto !important",
                                    width: "100% !important",
                                    height: "auto !important",
                                    overflow: "visible !important",
                                    visibility: "visible !important",
                                    pointerEvents: "auto !important",
                                    display: "block !important",
                                  },
                                }}
                              >
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "11pt",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <thead style={{ borderRadius: "0 !important" }}>
                                    <tr
                                      style={{
                                        backgroundColor: "#1976D2",
                                        color: "#ffffff",
                                        borderRadius: "0 !important",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "5px 12px",
                                          textAlign: "left",
                                          border: "1px solid #1565c0",
                                        }}
                                      >
                                        Type
                                      </th>
                                      <th
                                        style={{
                                          padding: "5px 12px",
                                          textAlign: "left",
                                          border: "1px solid #1565c0",
                                        }}
                                      >
                                        Document Name
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {documentRows.map((row, idx) => (
                                      <tr
                                        key={row.id}
                                        style={{
                                          backgroundColor: idx % 2 === 1 ? "#fafafa" : "#ffffff",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "5px 12px",
                                            border: "1px solid #e0e0e0",
                                            textAlign: "left",
                                            color: "#000",
                                          }}
                                        >
                                          {row.type}
                                        </td>
                                        <td
                                          style={{
                                            padding: "5px 12px",
                                            border: "1px solid #e0e0e0",
                                            textAlign: "left",
                                            color: "#000",
                                          }}
                                        >
                                          {row.documentName}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </Box>
                              <Paper
                                className="print-document-table"
                                elevation={0}
                                sx={{
                                  width: "100%",
                                  border: "1px solid #d9e4ec",
                                  borderRadius: "16px !important",
                                  overflow: "hidden",
                                  "@media print": {
                                    display: "none !important",
                                  },
                                  "@media screen": {
                                    display: "block",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    height: 56 + documentRows.length * 52,
                                    width: "100%",
                                    "@media print": {
                                      height: "auto !important",
                                      overflow: "visible !important",
                                    },
                                  }}
                                >
                                  <DataGrid
                                    rows={documentRows}
                                    columns={columns}
                                    hideFooter
                                    disableColumnMenu
                                    disableSelectionOnClick
                                    autoHeight
                                    getRowHeight={() => 52}
                                    sx={{
                                      border: "none",
                                      "& .MuiDataGrid-cell": {
                                        borderBottom: "1px solid #e7eef4",
                                        padding: "10px 16px",
                                        display: "flex",
                                        alignItems: "center",
                                      },
                                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                                        {
                                          outline: "none",
                                        },
                                      "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: "#0d4a63",
                                        color: "#ffffff",
                                        fontWeight: "bold",
                                        borderBottom: "none",
                                        minHeight: "56px !important",
                                        maxHeight: "56px !important",
                                      },
                                      "& .MuiDataGrid-columnHeader": {
                                        padding: "10px 16px",
                                        minHeight: "56px !important",
                                        maxHeight: "56px !important",
                                      },
                                      "& .MuiDataGrid-columnHeaderTitle": {
                                        color: "#ffffff",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                      },
                                      "& .MuiDataGrid-row:nth-of-type(even)": {
                                        backgroundColor: "#fafcfe",
                                      },
                                      "& .MuiDataGrid-row:hover": {
                                        backgroundColor: "#f1f7fb",
                                      },
                                    }}
                                  />
                                </Box>
                              </Paper>
                            </Box>
                          );
                        })()
                      ) : (
                        <Box
                          sx={{
                            border: "1px dashed #c7d7e2",
                            borderRadius: 2,
                            backgroundColor: "#fbfdff",
                            py: 5,
                            px: 3,
                            textAlign: "center",
                            "@media print": {
                              border: "none",
                              backgroundColor: "transparent",
                              py: 1,
                              px: 0,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", fontStyle: "italic" }}
                          >
                            No documents available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="overline" sx={sectionTitleSx}>
                    Status
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      ...screenCardSx,
                      p: 3,
                      "@media print": {
                        p: "4px 12px 0",
                      },
                    }}
                  >
                    <Grid container spacing={2}>
                      {statusItems.map((item) => (
                        <Grid item xs={12} md={4} key={item.label}>
                          <Box
                            sx={{
                              border: "1px solid #d9e4ec",
                              borderRadius: 2,
                              backgroundColor: "#fbfdff",
                              p: 2,
                              minHeight: 88,
                              "@media print": {
                                border: "none",
                                backgroundColor: "transparent",
                                borderRadius: 0,
                                p: "2px 0",
                                minHeight: "auto",
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "#5b7282",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                mb: 0.75,
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              {item.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          className="hide-on-print"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "grey.50",
            px: 3,
            py: 2,
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<PrintOutlinedIcon />}
            onClick={handlePrint}
            sx={{
              backgroundColor: "#2099d2",
              "&:hover": { backgroundColor: "#1a7ba8" },
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Print Page
          </Button>
          {(user?.userRoles.split(",").includes("Client Admin") ||
            user?.userRoles.split(",").includes("Client Accounting") ||
            user?.userRoles.split(",").includes("Customer User") ||
            user?.userRoles.split(",").includes("Client Processors") ||
            user?.userRoles.split(",").includes("State Reporter")) && (
            <Button
              variant="contained"
              startIcon={<DoneOutlinedIcon />}
              onClick={handleMarkAsDone}
              sx={{
                backgroundColor: selectedTab === 2 ? "#132D3D" : "#28a745",
                border: selectedTab === 2 ? "1px solid #28a745" : "none",
                color: "#fff",
                "&:hover": {
                  backgroundColor: selectedTab === 2 ? "#1a3d52" : "#218838",
                },
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {selectedTab === 2 ? "Return to Outstanding" : "Mark as Done"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog
        open={documentViewerOpen}
        onClose={handleCloseDocumentViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: "90%",
            maxHeight: "90vh",
            borderRadius: 2,
            boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
            overflow: "hidden",
          },
        }}
        sx={{ "& .MuiDialog-paper": { overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 2,
            px: 3,
            background: "linear-gradient(135deg, #0d4a63 0%, #15607a 100%)",
            color: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 24, opacity: 0.95 }} />
            <Typography
              component="span"
              variant="h6"
              sx={{
                fontWeight: 600,
                letterSpacing: "0.02em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {documentViewerFileName || "Document"}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDocumentViewer}
            sx={{
              color: "rgba(255,255,255,0.9)",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
              },
            }}
            aria-label="Close"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ padding: 0, height: "80vh", position: "relative" }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : documentViewerUrl ? (
            <Box sx={{ width: "100%", height: "100%" }}>
              <iframe
                src={documentViewerUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Document Viewer"
              />
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
